import { Table, TableBody, TableCell, TableRow } from "@mui/material";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import {
	getRowsPerPageOptions,
	StatusBadge,
	formatDate,
	getCodeId,
	getCodeDescription,
	capitalizeFirstLetter,
	mapDictionaryOptions,
} from "../../utils/functions";
import { Box } from "@mui/system";
import { useTheme } from "@emotion/react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { AMSModules, paginationStyle } from "../../utils/constants";
import InspectionsTableToolbar from "./InspectionsTableToolbar";
import AddEditInspectionDialog from "./AddEditInspectionDialog";
import InspectionDetailDialog from "./InspectionDetailDialog";
import {
	getInspectionsList,
	createInspection,
	updateInspection,
	deleteInspection,
	resolveInspection,
} from "../../reducers/InspectionsReducer";
import { notify } from "../../reducers/NotifierReducer";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { getEmployees, getFundingSources } from "../../utils/commonServices";
import { getVehicleOptions } from "../WorkOrders/useWorkOrdersData";
import {
	getChecklistItems,
} from "./useInspectionsData";
import { makeStyles } from "@mui/styles";
import { createWorkOrder } from "../../reducers/WorkOrdersReducer";
import PATViewButton from "../../components/Buttons/PATViewButton";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "desc" }];

export const initialFilterState = {
	status: null,
	type: [],
	vehicleId: [],
	perPersonnelId: [],
	period: null,
	from: null,
	to: null,
};

const useStyles = makeStyles((theme) => ({
	title: {
		color: theme.palette.text.primary,
		fontWeight: 600,
	},
	colorTitle: {
		color: theme.palette.mode === "dark" ? "#fff" : "#3b3e66",
	},
	cardBackground: {
		background:
			theme.palette.mode === "dark" ? "#1e2e3a" : "rgb(242, 242, 242)",
	},
}));

const Inspections = () => {
	const classes = useStyles();
	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();

	const { inspections, loading, total } = useSelector(
		(state) =>
			state?.inspections || {
				inspections: [],
				loading: false,
				total: 0,
			},
	);
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	const { codePerInspectionTypes, codePerWorkOrderTypes } = dictionary || {};

	const [search, setSearch] = useState("");
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo?.inspections || initialFilterState,
	);
	const [openDialog, setOpenDialog] = useState(false);
	const [showDetail, setShowDetail] = useState(false);
	const [selectedInspection, setSelectedInspection] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [employees, setEmployees] = useState([]);
	const [fundingSources, setFundingSources] = useState([]);
	const [vehicleOptions, setVehicleOptions] = useState([]);
	const [checklistItems, setChecklistItems] = useState([]);

	const currentAgency = useRef(null);

	const typeOptions = mapDictionaryOptions(codePerInspectionTypes) || [];
	const workOrderTypeOptions = mapDictionaryOptions(codePerWorkOrderTypes) || [];

	const handleLoadData = async () => {
		const [empList, fundingList, vehicles, checklist] =
			await Promise.all([
				getEmployees(userAgency, true),
				getFundingSources(userAgency, true),
				getVehicleOptions(userAgency),
				getChecklistItems(userAgency),
			]);

		setEmployees(empList);
		setFundingSources(fundingList);
		setVehicleOptions(vehicles);
		setChecklistItems(checklist);
	};

	useEffect(() => {
		if (userAgency && dictionary) {
			handleLoadData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency, dictionary]);

	useEffect(() => {
		if (selectedInspection && inspections.length > 0) {
			const updatedInspection = inspections.find(
				(v) => v.id === selectedInspection.id,
			);
			if (updatedInspection && updatedInspection !== selectedInspection) {
				setSelectedInspection(updatedInspection);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inspections]);

	const refreshList = useCallback(() => {
		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			dispatch(
				getInspectionsList(
					page,
					pageSize,
					field,
					sort,
					search,
					filters,
					userAgency,
				),
			);
		}
	}, [dispatch, page, pageSize, sortModel, search, filters, userAgency]);

	useEffect(() => {
		if (!settingsLoaded || !isAuthenticated) return;

		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}

			dispatch(
				getInspectionsList(
					page,
					pageSize,
					field,
					sort,
					search,
					isAgencyChanged ? initialFilterState : filters,
					userAgency,
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		search,
		filters,
		settingsLoaded,
		isAuthenticated,
		userAgency,
	]);

	// Get vehicle info for display
	const getVehicleDisplay = useCallback(
		(vehicleId) => {
			const vehicle = vehicleOptions.find((v) => v.value === vehicleId);
			return vehicle ? vehicle.label : vehicleId || "—";
		},
		[vehicleOptions],
	);

	// Get employee info for display
	const getEmployeeDisplay = useCallback(
		(employeeId) => {
			const employee = employees.find(
				(e) => e.value === employeeId || e.value === String(employeeId),
			);
			return employee ? employee.label : "—";
		},
		[employees],
	);

	const baseColumns = useMemo(
		() => [
			{
				field: "id",
				headerName: "ID",
				width: 80,
				sortable: true,
			},
			{
				field: "vehicleId",
				headerName: "Vehicle",
				width: 200,
				sortable: true,
				renderCell: ({ row }) => getVehicleDisplay(row.vehicleId),
			},
			{
				field: "perPersonnelId",
				headerName: "Employee",
				width: 180,
				sortable: true,
				renderCell: ({ row }) => getEmployeeDisplay(row.perPersonnelId),
			},
			{
				field: "type",
				headerName: "Type",
				width: 130,
				sortable: true,
				renderCell: ({ row }) => {
					const typeValue = row?.typeInfo?.Code || row?.type;
					return (
						<StatusBadge
							status={capitalizeFirstLetter(
								getCodeDescription(
									typeValue,
									dictionary.codePerInspectionTypes,
								),
							)}
						/>
					);
				},
			},
			{
				field: "date",
				headerName: "Date & Time",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => formatDate(row?.date, true),
			},
			{
				field: "odometer",
				headerName: "Odometer",
				width: 120,
				sortable: true,
				renderCell: ({ row }) =>
					row?.odometer ? `${row.odometer.toLocaleString()} mi` : "—",
			},
			{
				field: "pass",
				headerName: "Pass",
				width: 80,
				sortable: false,
				renderCell: ({ row }) => {
					const passCount =
						row?.responses?.filter((r) => r.status === "Pass")
							?.length || 0;
					return passCount;
				},
			},
			{
				field: "fail",
				headerName: "Fail",
				width: 80,
				sortable: false,
				renderCell: ({ row }) => {
					const failCount =
						row?.responses?.filter((r) => r.status === "Fail")
							?.length || 0;
					return failCount;
				},
			},
			{
				field: "overallStatus",
				headerName: "Result",
				width: 120,
				sortable: true,
				renderCell: ({ row }) => (
					<StatusBadge status={row?.overallStatus} />
				),
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 120,
				align: "center",
				sortable: false,
				renderCell: ({ row }) =>
					row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATViewButton
											tooltipText="View Inspection"
											onClick={(e) => {
												e.stopPropagation();
												setSelectedInspection(row);
												setShowDetail(true);
											}}
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Inspection"
											onClick={(e) => {
												e.stopPropagation();
												setSelectedId(row.id);
												setSelectedInspection(row);
												setOpenDeleteDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history, getVehicleDisplay, getEmployeeDisplay, dictionary],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["inspectionsTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["inspectionsTableColumnOrder"]),
				);
			}

			if (settingsMap["inspectionsTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["inspectionsTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["inspectionsTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(settingsMap["inspectionsTableColumnVisibility"]),
				);
			}

			if (settingsMap["inspectionsTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["inspectionsTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"inspectionsTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"inspectionsTableColumnOrder",
						JSON.stringify(newColumnOrder),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => ({
			field: col.field,
			width: col.width,
		}));
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"inspectionsTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"inspectionsTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.Inspections, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({ type: AMSModules.Inspections, payload: initialFilterState });
	};

	// Handle save inspection
	const handleSaveInspection = async (payload) => {
		try {
			const typeId = getCodeId(payload.type, codePerInspectionTypes);

			const data = {
				vehicleId: payload.vehicleId,
				perPersonnelId: payload.perPersonnelId,
				type: payload.type,
				typeId,
				date: payload.date,
				odometer: payload.odometer,
				overallStatus: payload.overallStatus,
				notes: payload.notes,
			};

			if (selectedInspection) {
				await dispatch(
					updateInspection(
						selectedInspection.id,
						data,
						payload.responses,
						payload.photos,
						userAgency,
						userData.user.ptsUserID,
						selectedInspection,
					),
				);
			} else {
				await dispatch(
					createInspection(
						data,
						payload.responses,
						payload.photos,
						userAgency,
						userData.user.ptsUserID,
					),
				);
			}

			setOpenDialog(false);
			setSelectedInspection(null);
			refreshList();
			dispatch(notify("Inspection saved successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to save inspection", "error"));
		}
	};

	// Handle resolve inspection
	const handleResolveInspection = async (resolveData) => {
		try {
			await dispatch(
				resolveInspection(
					selectedInspection.id,
					resolveData,
					userData.user.ptsUserID,
					userAgency,
					selectedInspection,
				),
			);

			// Update the selected inspection locally for the detail dialog
			setSelectedInspection((prev) => ({
				...prev,
				resolvedAt: resolveData.resolvedAt,
				resolvedBy: userData.user.ptsUserID,
				resolvedComments: resolveData.resolvedComments,
			}));

			dispatch(notify("Inspection resolved successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to resolve inspection", "error"));
		}
	};

	// Handle create work order from inspection
	const handleCreateWorkOrder = async (woData) => {
		try {
			const payload = {
				vehicleId: selectedInspection.vehicleId,
				type: woData.type,
				status: "Open",
				vendor: woData.vendor,
				cost: woData.cost,
				notes: woData.notes,
				fundingSourceId: woData.fundingSourceID,
				openedDate: new Date().toISOString(),
			};

			await dispatch(
				createWorkOrder(payload, userAgency, userData.user.ptsUserID),
			);

			// Optionally update vehicle status
			if (woData.flagVehicle && selectedInspection.vehicleId) {
				const vehicleService =
					await import("../../reducers/service").then((m) =>
						m.getService("per-vehicles"),
					);
				await vehicleService.patch(selectedInspection.vehicleId, {
					status: "Maintenance",
				});
			}

			dispatch(notify("Work order created successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to create work order", "error"));
		}
	};

	// Handle delete
	const handleDelete = async () => {
		try {
			await dispatch(
				deleteInspection(
					selectedId,
					userData.user.ptsUserID,
					userAgency,
					selectedInspection,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
			setSelectedInspection(null);
			dispatch(notify("Inspection deleted successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to delete inspection", "error"));
		}
	};

	// Stats calculations
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const todayCount = inspections.filter(
		(i) => new Date(i.date) >= today,
	).length;

	const failedThisWeek = inspections.filter((i) => {
		const d = new Date(i.date);
		const weekAgo = new Date(Date.now() - 7 * 86400000);
		return i.overallStatus === "Fail" && d >= weekAgo;
	}).length;

	const inspectedVehicleIDs = new Set(
		inspections
			.filter(
				(i) => new Date(i.date) >= new Date(Date.now() - 24 * 3600000),
			)
			.map((i) => i.vehicleId),
	);

	const inServiceVehicles = vehicleOptions.filter(
		(v) => v.status === "In Service",
	);
	const noInspection24h = inServiceVehicles.filter(
		(v) => !inspectedVehicleIDs.has(v.value),
	).length;

	// Prepare print data
	const printInspections = useMemo(() => {
		return inspections.map((insp) => ({
			...insp,
			vehicle: getVehicleDisplay(insp.vehicleId),
			employee: getEmployeeDisplay(insp.perPersonnelId),
			typeDisplay: insp?.typeInfo?.Code || insp?.type || "—",
			dateFormatted: formatDate(insp.date, true),
			odometerFormatted: insp.odometer
				? `${insp.odometer.toLocaleString()} mi`
				: "—",
			passCount:
				insp.responses?.filter((r) => r.status === "Pass")?.length || 0,
			failCount:
				insp.responses?.filter((r) => r.status === "Fail")?.length || 0,
		}));
	}, [inspections, getVehicleDisplay, getEmployeeDisplay]);

	return (
		<>
			{/* Stats Cards */}
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(3,1fr)",
					gap: 14,
					marginBottom: 22,
				}}
			>
				<div
					className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} blue`}
				>
					<div className="kpi-label">Inspections Today</div>
					<div className="kpi-value">{todayCount}</div>
					<div className="kpi-sub">completed this shift</div>
				</div>
				<div
					className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} ${noInspection24h > 0 ? "yellow" : "green"}`}
				>
					<div className="kpi-label">No Inspection (24h)</div>
					<div className="kpi-value">{noInspection24h}</div>
					<div className="kpi-sub">in-service vehicles</div>
				</div>
				<div
					className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} ${failedThisWeek > 0 ? "red" : "green"}`}
				>
					<div className="kpi-label">Failed This Week</div>
					<div className="kpi-value">{failedThisWeek}</div>
					<div className="kpi-sub">inspection failures</div>
				</div>
			</div>

			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 85px)",
					marginTop: theme.spacing(0.5),
				}}
			>
				<InspectionsTableToolbar
					onAddClick={() => {
						setSelectedInspection(null);
						setOpenDialog(true);
					}}
					vehicleOptions={vehicleOptions}
					employeeOptions={employees}
					typeOptions={typeOptions}
					search={search}
					setSearch={setSearch}
					inspections={printInspections}
					resetFilters={resetFilters}
					filters={filters}
					onFilterChange={handleOnFilterChange}
				/>

				<Box
					sx={{
						flex: 1,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<DataGridPro
						rows={inspections}
						columns={columns}
						initialState={{ pinnedColumns: { right: ["actions"] } }}
						getRowId={(row) => row.GUID || row.id}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={
							handleColumnVisibilityModelChange
						}
						pageSize={pageSize}
						rowsPerPageOptions={rowsPerPageOptions}
						pagination
						paginationMode="server"
						sortModel={sortModel}
						sortingMode="server"
						onSortModelChange={handleSortModelChange}
						rowCount={total}
						onRowClick={(params) => {
							setSelectedInspection(params?.row);
							setShowDetail(true);
						}}
						page={page}
						onPageChange={setPage}
						onPageSizeChange={setPageSize}
						loading={loading}
						disableSelectionOnClick
						onColumnOrderChange={handleColumnOrderChange}
						onColumnWidthChange={handleColumnWidthChange}
						sx={{
							...paginationStyle(theme, themeMode),
							color: theme.palette.colorFont,
							margin: 1,
							"& .MuiDataGrid-row:hover": {
								cursor: "pointer",
							},
						}}
					/>
				</Box>
			</Box>

			{/* Add/Edit Inspection Dialog */}
			<AddEditInspectionDialog
				open={openDialog}
				onClose={() => {
					setOpenDialog(false);
					setSelectedInspection(null);
				}}
				onSave={handleSaveInspection}
				initialData={selectedInspection}
				vehicles={vehicleOptions}
				employees={employees}
				checklistItems={checklistItems}
				inspectionTypeOptions={typeOptions}
			/>

			{/* Inspection Detail Dialog */}
			<InspectionDetailDialog
				open={showDetail}
				onClose={() => {
					setShowDetail(false);
					setSelectedInspection(null);
				}}
				inspection={selectedInspection}
				vehicles={vehicleOptions}
				employees={employees}
				checklistItems={checklistItems}
				fundingSources={fundingSources}
				onResolveInspection={handleResolveInspection}
				onCreateWorkOrder={handleCreateWorkOrder}
				workOrderTypeOptions={workOrderTypeOptions}
				fundingSourceOptions={fundingSources}
			/>

			{/* Delete Confirmation Dialog */}
			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedId(null);
					setSelectedInspection(null);
				}}
				onConfirm={handleDelete}
				title="Delete Inspection"
				message="Are you sure you want to delete this inspection?"
			/>
		</>
	);
};

export default Inspections;
